import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { usePortal } from '@/contexts/PortalContext'
import { TemplateSection, TemplateField } from '@/lib/portalApi'
import { CertificationSummaryModal } from './CertificationSummaryModal'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { InlineFieldTooltip } from './InlineFieldTooltip'
import { AutoSaveIndicator } from './AutoSaveIndicator'
import { DocumentUploadZone } from './DocumentUploadZone'

export function SupplierFormView() {
  const {
    data,
    answers,
    isSaving,
    lastSavedAt,
    updateAnswer,
    submitForm,
    hasUnreviewedExtractions,
    isComplementMode,
    requestedFieldIdsSet,
  } = usePortal()
  const [isCertificationOpen, setIsCertificationOpen] = useState(false)

  // Navigation entre sections — mémoïsé pour stabiliser les deps des useMemo/useCallback en aval
  const sections = useMemo<TemplateSection[]>(
    () =>
      (data?.template.sections ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data]
  )

  // Story 3.3 AC #9 — helper : un champ est éditable s'il n'est pas en mode complément,
  // ou s'il fait partie des `requestedFieldIds` demandés
  const isEditable = useCallback(
    (f: TemplateField) => !isComplementMode || requestedFieldIdsSet.has(f.id),
    [isComplementMode, requestedFieldIdsSet]
  )

  // Story 3.3 AC #9 — sections avec au moins un champ éditable en mode complément
  const sectionHasEditableField = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const s of sections) {
      map.set(s.id, s.fields.some(isEditable))
    }
    return map
  }, [sections, isEditable])

  // Story 3.3 AC #11 — graceful degradation : un fournisseur peut atterrir sur
  // un portail dont la `ComplementRequest` cible des champs supprimés du
  // template (template muté entre la demande et l'accès). Si aucun champ
  // ciblé n'est résolvable, on affiche un état vide plutôt que de retomber
  // silencieusement sur la section 0 (qui n'aurait que des readonly).
  const hasAnyEditableField = useMemo(
    () => sections.some((s) => sectionHasEditableField.get(s.id)),
    [sections, sectionHasEditableField]
  )

  // Story 3.3 AC #9 — démarrer sur la première section contenant au moins un champ éditable
  const initialSectionIndex = useMemo(() => {
    if (!isComplementMode) return 0
    const idx = sections.findIndex((s) => sectionHasEditableField.get(s.id))
    return idx >= 0 ? idx : 0
  }, [isComplementMode, sections, sectionHasEditableField])

  const [currentSectionIndex, setCurrentSectionIndex] = useState(initialSectionIndex)

  // P-5 fix : si data arrive async et change initialSectionIndex, rattraper la valeur
  // Garde : ne ré-initialiser que si l'utilisateur n'a pas encore navigué manuellement
  const [hasNavigated, setHasNavigated] = useState(false)
  useEffect(() => {
    if (!hasNavigated) {
      setCurrentSectionIndex(initialSectionIndex)
    }
  }, [initialSectionIndex, hasNavigated])

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({})
  const fieldRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())

  // UX-8 — Capture de la dernière valeur sélectionnée dans un Select pour valider
  // sur onOpenChange(false) (équivalent onBlur pour les champs select)
  const pendingSelectValues = useRef<Map<string, string>>(new Map())

  // Résumé d'erreurs
  const [attemptedNavigation, setAttemptedNavigation] = useState(false)

  const currentSection = sections[currentSectionIndex]

  // Story 3.3 AC #9 — en mode complément, les sections sans champ éditable
  // sont skippées par la navigation. Le bouton « Section suivante » doit
  // pointer vers la prochaine section éditable (pas la suivante absolue),
  // et le bouton « Soumettre » apparaît dès qu'il n'y a plus de section
  // éditable après la courante (et non sur l'index absolu max).
  // Sans cet ajustement, un fournisseur dont les `requestedFieldIds` n'étaient
  // que sur la première section d'un template multi-sections ne pouvait
  // ni naviguer ni soumettre (bug observé en smoke 2026-05-20).
  const nextEditableSectionIndex = useMemo(() => {
    for (let i = currentSectionIndex + 1; i < sections.length; i++) {
      const s = sections[i]
      if (!isComplementMode || sectionHasEditableField.get(s.id)) return i
    }
    return -1
  }, [currentSectionIndex, sections, sectionHasEditableField, isComplementMode])

  const prevEditableSectionIndex = useMemo(() => {
    for (let i = currentSectionIndex - 1; i >= 0; i--) {
      const s = sections[i]
      if (!isComplementMode || sectionHasEditableField.get(s.id)) return i
    }
    return -1
  }, [currentSectionIndex, sections, sectionHasEditableField, isComplementMode])

  const isLastSection = nextEditableSectionIndex === -1

  // Story 3.3 AC #15 — focus initial sur le premier champ éditable en mode complément
  useEffect(() => {
    if (!isComplementMode || !currentSection) return
    const firstEditable = currentSection.fields.find(isEditable)
    if (!firstEditable) return
    // Delay : le DOM doit être monté
    const t = setTimeout(() => {
      const el = document.getElementById(firstEditable.id)
      if (el) (el as HTMLElement).focus()
    }, 50)
    return () => clearTimeout(t)
  }, [currentSectionIndex, currentSection, isComplementMode, isEditable])

  function validateField(field: TemplateField, value: string | number | null): string {
    // Story 3.3 AC #9 — en mode complément, on ne valide `required` que sur les champs éditables
    if (isComplementMode && !isEditable(field)) return ''
    const strVal = value === null ? '' : String(value)
    if (field.required && strVal.trim() === '') {
      return 'Ce champ est obligatoire'
    }
    if (field.type === 'number' && strVal.trim() !== '' && isNaN(Number(strVal))) {
      return 'Veuillez saisir un nombre valide'
    }
    return ''
  }

  function handleBlur(field: TemplateField, value: string | number | null) {
    const errorMsg = validateField(field, value)
    setErrors(prev => {
      if (errorMsg) return { ...prev, [field.id]: errorMsg }
      const next = { ...prev }
      delete next[field.id]
      return next
    })
  }

  function handleNavigate(targetIndex: number) {
    if (targetIndex < 0) return
    // Story 3.3 AC #9 — en mode complément, ignorer les sections sans champ éditable
    const target = sections[targetIndex]
    if (isComplementMode && target && !sectionHasEditableField.get(target.id)) return

    if (targetIndex > currentSectionIndex) {
      // MEDIUM-5 — Valider TOUS les champs de la section courante à la tentative de navigation
      // (pas seulement ceux déjà touchés via onBlur) pour que CA-3 s'active même si
      // le fournisseur n'a jamais quitté un champ (CA: #3)
      const sectionErrors: Record<string, string> = {}
      for (const field of currentSection.fields) {
        const errorMsg = validateField(field, answers[field.id] ?? null)
        if (errorMsg) sectionErrors[field.id] = errorMsg
      }

      // Fusionner avec les erreurs existantes (peut en effacer si un champ est maintenant valide)
      const mergedErrors = { ...errors, ...sectionErrors }
      // Retirer les champs valides de la section qui ne sont plus en erreur
      for (const field of currentSection.fields) {
        if (!sectionErrors[field.id]) delete mergedErrors[field.id]
      }
      setErrors(mergedErrors)

      const totalErrorCount = Object.keys(mergedErrors).length
      if (totalErrorCount > 3) {
        setAttemptedNavigation(true)
        const firstErrorId = Object.keys(mergedErrors)[0]
        fieldRefs.current.get(firstErrorId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
    }
    setHasNavigated(true)
    setCurrentSectionIndex(targetIndex)
    setAttemptedNavigation(false)
  }

  function renderReadonlyField(field: TemplateField) {
    const rawValue = answers[field.id]
    // AC #9 : ne pas rendre si valeur vide pour éviter le bruit visuel
    if (rawValue === null || rawValue === undefined || String(rawValue).trim() === '') return null
    const displayValue = field.type === 'number' && field.unit
      ? `${rawValue} ${field.unit}`
      : String(rawValue)
    return (
      <div
        key={field.id}
        ref={el => { fieldRefs.current.set(field.id, el) }}
        className="space-y-1"
        aria-label="Champ déjà renseigné"
      >
        <div className="flex items-center gap-1 flex-wrap">
          <Label>{field.label}</Label>
          <Badge variant="secondary" className="text-xs">Déjà renseigné</Badge>
        </div>
        <div className="readonly-field">
          {displayValue}
        </div>
      </div>
    )
  }

  function renderEditableField(field: TemplateField) {
    const value = answers[field.id] ?? ''
    const errorMsg = errors[field.id]
    const inputClasses = 'min-h-[48px]'
    const ariaLabel = isComplementMode
      ? `Champ à compléter : ${field.label}`
      : undefined

    const input = field.type === 'select' ? (
      // UX-8 — Pour les champs select, la validation se déclenche sur onOpenChange(false)
      // (fermeture du dropdown = équivalent onBlur) et non sur onValueChange (= onChange).
      // La valeur sélectionnée est capturée dans pendingSelectValues pour éviter la closure stale.
      <Select
        value={String(value)}
        onValueChange={val => {
          updateAnswer(field.id, val)
          pendingSelectValues.current.set(field.id, val)
        }}
        onOpenChange={open => {
          if (!open) {
            const pendingVal = pendingSelectValues.current.get(field.id) ?? String(answers[field.id] ?? '')
            handleBlur(field, pendingVal)
          }
        }}
      >
        <SelectTrigger className={inputClasses} id={field.id} aria-label={ariaLabel}>
          <SelectValue placeholder="Sélectionner..." />
        </SelectTrigger>
        <SelectContent>
          {(field.options ?? []).map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <Input
        id={field.id}
        type={field.type === 'file' ? 'text' : field.type}
        placeholder={
          field.type === 'file'
            ? 'Upload IA disponible via le panneau ci-dessus'
            : undefined
        }
        className={inputClasses}
        value={String(value)}
        aria-label={ariaLabel}
        onChange={e => updateAnswer(field.id, e.target.value)}
        onBlur={e => handleBlur(field, e.target.value)}
      />
    )

    return (
      <div
        key={field.id}
        ref={el => { fieldRefs.current.set(field.id, el) }}
        className={isComplementMode ? 'complement-field space-y-1' : 'space-y-1'}
      >
        <div className="flex items-center gap-1 flex-wrap">
          <Label htmlFor={field.id}>{field.label}</Label>
          {field.required ? (
            <Badge variant="destructive" className="text-xs">Requis</Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Optionnel</Badge>
          )}
          {isComplementMode && (
            <Badge variant="default" className="complement-badge text-xs">
              À compléter
            </Badge>
          )}
          <InlineFieldTooltip description={field.description} unit={field.unit} required={field.required} />
        </div>

        {input}

        {errorMsg && (
          <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </p>
        )}
      </div>
    )
  }

  function renderField(field: TemplateField) {
    if (isEditable(field)) return renderEditableField(field)
    return renderReadonlyField(field)
  }

  if (!currentSection) return null

  const errorCount = Object.keys(errors).length

  // Story 3.3 AC #9 — séparer les champs éditables des readonly pour placer le heading h2 avant
  const editableFields = currentSection.fields.filter(isEditable)
  const readonlyFields = currentSection.fields.filter((f) => !isEditable(f))

  return (
    <div className="w-full">
      {/* AutoSaveIndicator (CA-5) */}
      <div className="mb-4">
        <AutoSaveIndicator isSaving={isSaving} lastSavedAt={lastSavedAt} />
      </div>

      {/* Upload IA (Story 5.1) — disponible aussi en mode complément : l'extraction
          IA peut renseigner les `requestedFieldIds` (les autres extractions sont
          ignorées côté UI car les champs hors périmètre sont readonly). */}
      <DocumentUploadZone />

      {/* Story 3.3 AC #11 — état vide quand la demande de complément cible
          des champs introuvables dans le template (édition du template entre
          la demande et l'accès). Court-circuite le rendu standard. */}
      {isComplementMode && !hasAnyEditableField && (
        <div
          role="alert"
          className="rounded border bg-amber-50 border-amber-200 px-4 py-6 text-sm text-amber-900"
        >
          <p className="font-semibold mb-1">Aucun champ à compléter</p>
          <p>
            Les champs ciblés par cette demande ne sont plus disponibles dans le
            formulaire. Contactez votre interlocuteur ({data?.organization.name}) pour
            obtenir un nouveau lien.
          </p>
        </div>
      )}

      {/* Résumé d'erreurs (CA-3) */}
      {attemptedNavigation && errorCount > 3 && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-4">
          {errorCount} erreurs à corriger avant de continuer
        </div>
      )}

      {/* Barre de navigation par sections (CA-7) — sections sans champ éditable grisées en mode complément */}
      {sections.length > 1 && (
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
          {sections.map((section, idx) => {
            const sectionEnabled = !isComplementMode || sectionHasEditableField.get(section.id)
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handleNavigate(idx)}
                disabled={!sectionEnabled}
                aria-disabled={!sectionEnabled || undefined}
                aria-label={
                  !sectionEnabled
                    ? `${section.title} — aucun champ à compléter dans cette section`
                    : undefined
                }
                title={!sectionEnabled ? 'Aucun champ à compléter dans cette section' : undefined}
                className={[
                  'flex-shrink-0 px-3 py-2 text-sm rounded-t font-medium transition-colors',
                  idx === currentSectionIndex
                    ? 'border-b-2 text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                  !sectionEnabled ? 'opacity-40 cursor-not-allowed' : '',
                ].join(' ')}
                style={
                  idx === currentSectionIndex
                    ? { borderBottomColor: 'var(--supplier-primary)' }
                    : undefined
                }
              >
                {section.title}
              </button>
            )
          })}
        </div>
      )}

      {/* Story 3.3 AC #15 — heading avant les champs éditables en mode complément */}
      {isComplementMode && editableFields.length > 0 && (
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--supplier-text)' }}>
          Champs à compléter
        </h2>
      )}

      {/* Champs éditables d'abord */}
      <div className="space-y-6">
        {editableFields.map((field) => renderField(field))}
      </div>

      {/* Story 3.3 AC #9 — rappel des champs déjà renseignés (readonly) en mode complément */}
      {isComplementMode && readonlyFields.some((f) => {
        const v = answers[f.id]
        return v !== null && v !== undefined && String(v).trim() !== ''
      }) && (
        <>
          <h3 className="text-sm font-semibold mt-8 mb-3 text-muted-foreground">
            Déjà renseignés
          </h3>
          <div className="space-y-4">
            {readonlyFields.map((field) => renderField(field))}
          </div>
        </>
      )}

      {/* Navigation précédente / suivante */}
      <div className="flex justify-between mt-8 gap-4">
        <button
          type="button"
          onClick={() => handleNavigate(prevEditableSectionIndex)}
          disabled={prevEditableSectionIndex < 0}
          className="px-4 py-2 text-sm rounded border disabled:opacity-40 min-h-[48px]"
        >
          Section précédente
        </button>

        {isLastSection ? (
          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              disabled={
                Object.values(errors).some(Boolean) ||
                hasUnreviewedExtractions ||
                (isComplementMode && !hasAnyEditableField)
              }
              onClick={() => setIsCertificationOpen(true)}
              className="px-6 py-2 text-sm rounded text-white font-medium disabled:opacity-40 min-h-[48px]"
              style={{ backgroundColor: 'var(--supplier-primary-strong)' }}
            >
              {isComplementMode ? 'Soumettre mon complément' : 'Vérifier et envoyer'}
            </button>
            {hasUnreviewedExtractions && (
              <p className="text-sm text-amber-600 mt-1">
                Veuillez confirmer ou rejeter toutes les valeurs extraites par l'IA avant de certifier vos données
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => handleNavigate(nextEditableSectionIndex)}
            className="px-4 py-2 text-sm rounded text-white font-medium min-h-[48px]"
            style={{ backgroundColor: 'var(--supplier-primary-strong)' }}
          >
            Section suivante
          </button>
        )}
      </div>
      <CertificationSummaryModal
        isOpen={isCertificationOpen}
        onClose={() => setIsCertificationOpen(false)}
        onConfirm={submitForm}
      />
    </div>
  )
}
